import { useRestoreRecordingMutation } from '@carboncopyco/data';
import {
  ArchiveIcon,
  CopyOutlineIcon,
  DuplicateOutlineIcon,
  MoreVerticalOutlineIcon,
  ShowOutlineIcon,
  SubdirectoryRightOutlineIcon,
} from '@carboncopyco/icons';
import {
  type IRecording,
  PermissionAction,
  PermissionKey,
} from '@carboncopyco/types';
import { Button, Dropdown, DropdownItem, notify } from '@carboncopyco/ui';
import { useCloneAnalytics } from '@carboncopyco/utils';
import { useBoolean } from 'ahooks';
import { type ReactNode, isValidElement } from 'react';
import {
  ArchiveCloneModal,
  CopyCloneModalWithFolders,
  DuplicateCloneModal,
  MoveCloneModal,
} from '../../../../AssetActions/Clone';
import { usePermissionsContext } from '../../../../utils';
import { useWorkspacePageProps } from '../../WorkspacePageContext';
import { ArchiveTileDropdown } from '../ArchiveTileDropdown';

export const CloneActionsDropdown = ({
  archived,
  clone,
  inFolder,
  handleMenuOpen,
  triggerElement,
}: {
  clone: IRecording;
  archived?: boolean;
  inFolder?: boolean;
  handleMenuOpen: (isOpen: boolean) => void;
  triggerElement?: ReactNode;
}) => {
  const [isMenuOpen, { toggle: toggleMenu }] = useBoolean(false);
  const { workspaceId, organizationId, handleClonePreview, folderId } =
    useWorkspacePageProps();

  const { trackCloneClickDuplicate, trackCloneClickArchive } =
    useCloneAnalytics({
      location: folderId === '0' ? 'workspace_page' : 'folder_page',
      cloneId: clone?.id,
    });
  const [showDuplicateCloneModal, setShowDuplicateCloneModal] =
    useBoolean(false);
  const [showCopyCloneModal, setShowCopyCloneModal] = useBoolean(false);
  const [showArchiveCloneModal, setShowArchiveCloneModal] = useBoolean(false);
  const [restoreClone] = useRestoreRecordingMutation();
  const { getPermissions } = usePermissionsContext();
  const [showMoveCloneModal, setShowMoveCloneModal] = useBoolean(false);

  const duplicateClonePermission = getPermissions(
    PermissionKey.record,
    PermissionAction.duplicateRecord
  );

  const copyToWorkspacePermission = getPermissions(
    PermissionKey.record,
    PermissionAction.copyRecord
  );
  const deleteClonePermission = getPermissions(
    PermissionKey.record,
    PermissionAction.deleteRecordingById
  );
  const moveClonePermission = getPermissions(
    PermissionKey.record,
    PermissionAction.moveRecord
  );

  return (
    <div onClick={(event) => event.stopPropagation()}>
      {archived ? (
        <ArchiveTileDropdown
          inFolder={inFolder}
          restore={() => {
            restoreClone({ id: clone.id, organizationId })
              .unwrap()
              .then(() => {
                notify.success(
                  `Clone "${clone.recordName}" was successfully restored`
                );
              })
              .catch(() => {
                notify.error(`Error restoring clone "${clone.recordName}"`);
              });
          }}
          preview={() => {
            toggleMenu();
            handleClonePreview(clone);
          }}
          open={isMenuOpen}
          toggleOpen={() => {
            handleMenuOpen(!isMenuOpen);
            toggleMenu();
          }}
        />
      ) : (
        <Dropdown
          asChild
          open={isMenuOpen}
          onOpenChange={() => {
            handleMenuOpen(!isMenuOpen);
            toggleMenu();
          }}
          triggerElement={
            isValidElement(triggerElement) ? (
              triggerElement
            ) : (
              <Button
                small
                dark
                icon={<MoreVerticalOutlineIcon />}
              />
            )
          }
        >
          <div className="p-2 flex flex-col">
            <DropdownItem
              iconLeft={<ShowOutlineIcon />}
              label="Preview"
              onClick={(event) => {
                event.preventDefault();
                toggleMenu();
                handleMenuOpen(isMenuOpen);
                handleClonePreview(clone);
              }}
            />
            {duplicateClonePermission && (
              <DropdownItem
                iconLeft={<DuplicateOutlineIcon size={20} />}
                label="Duplicate"
                onClick={() => {
                  trackCloneClickDuplicate();
                  setShowDuplicateCloneModal.setTrue();
                }}
              />
            )}
            {moveClonePermission && (
              <DropdownItem
                iconLeft={<SubdirectoryRightOutlineIcon size={18} />}
                label="Move to..."
                onClick={setShowMoveCloneModal.setTrue}
              />
            )}
            {copyToWorkspacePermission && (
              <DropdownItem
                iconLeft={<CopyOutlineIcon size={20} />}
                label="Copy to..."
                onClick={setShowCopyCloneModal.setTrue}
              />
            )}
            {deleteClonePermission && (
              <DropdownItem
                iconLeft={<ArchiveIcon size={20} />}
                label="Archive"
                onClick={() => {
                  trackCloneClickArchive();
                  setShowArchiveCloneModal.setTrue();
                }}
              />
            )}
          </div>
        </Dropdown>
      )}
      {showDuplicateCloneModal && (
        <DuplicateCloneModal
          organizationId={organizationId}
          cloneId={clone.id}
          onClose={setShowDuplicateCloneModal.setFalse}
          show={showDuplicateCloneModal}
        />
      )}
      {showCopyCloneModal && (
        <CopyCloneModalWithFolders
          organizationId={organizationId}
          clone={clone}
          onClose={setShowCopyCloneModal.setFalse}
        />
      )}
      {showArchiveCloneModal && (
        <ArchiveCloneModal
          folderId={String(clone.folderId)}
          organizationId={organizationId}
          cloneId={clone.id}
          show={showArchiveCloneModal}
          onClose={setShowArchiveCloneModal.setFalse}
        />
      )}
      {showMoveCloneModal && (
        <MoveCloneModal
          clone={clone}
          onClose={setShowMoveCloneModal.setFalse}
          workspaceId={workspaceId}
          organizationId={organizationId}
        />
      )}
    </div>
  );
};
